import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getRepliesByAnnotation,
  createReply,
  deleteReply,
  replyBodySchema,
} from '@/lib/api/replies';
import { useAuth } from '@/lib/auth/useAuth';
import { ReactionBar } from './ReactionBar';

interface Props {
  annotationId: string;
}

export function ReplyThread({ annotationId }: Props) {
  const { status } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ['replies', annotationId];

  const [composing, setComposing] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const { data: replies = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => getRepliesByAnnotation(annotationId),
  });

  const createMutation = useMutation({
    mutationFn: (body: string) => createReply(annotationId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setDraftText('');
      setComposing(false);
      setValidationError(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (replyId: string) => deleteReply(replyId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const result = replyBodySchema.safeParse(draftText.trim());
    if (!result.success) {
      setValidationError(result.error.issues[0]?.message ?? 'Invalid reply');
      return;
    }
    setValidationError(null);
    createMutation.mutate(result.data);
  }

  const canReply = status === 'authenticated';

  return (
    <div className="mt-2 flex flex-col gap-2">
      {isLoading && (
        <div className="flex justify-center py-2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-200 border-t-gray-500" />
        </div>
      )}

      {replies.map(reply => (
        <div key={reply.id} className="flex flex-col gap-1 rounded-lg bg-gray-50 px-2.5 py-2">
          <div className="flex items-center gap-1.5">
            <div
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[0.5rem] font-bold text-white"
              style={{ background: reply.color }}
            >
              {reply.initials}
            </div>
            <span className="text-[0.72rem] font-medium text-gray-700">{reply.authorName}</span>
            <span className="ml-auto text-[0.65rem] text-gray-400">
              {new Date(reply.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
            {reply.isOwn && (
              <button
                onClick={() => deleteMutation.mutate(reply.id)}
                disabled={deleteMutation.isPending}
                aria-label="Delete reply"
                className="ml-1 flex h-5 w-5 items-center justify-center rounded text-gray-300 transition-colors hover:text-red-400 disabled:opacity-40"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M18 6 6 18M6 6l12 12"/>
                </svg>
              </button>
            )}
          </div>
          <p className="text-xs leading-relaxed text-gray-800">{reply.bodyMd}</p>
          <ReactionBar targetType="reply" targetId={reply.id} />
        </div>
      ))}

      {canReply && !composing && (
        <button
          onClick={() => setComposing(true)}
          className="self-start rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-500 transition-colors hover:border-gray-300 hover:bg-gray-50"
        >
          Reply…
        </button>
      )}

      {composing && (
        <form onSubmit={submit} className="flex flex-col gap-1.5">
          <textarea
            value={draftText}
            onChange={e => setDraftText(e.target.value)}
            placeholder="Write a reply…"
            rows={3}
            maxLength={1000}
            autoFocus
            className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-xs outline-none transition-colors focus:border-[#1a56a0] focus:ring-1 focus:ring-[#1a56a0]"
          />
          {validationError && <p className="text-[0.65rem] text-red-500">{validationError}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setComposing(false); setDraftText(''); setValidationError(null); }}
              className="rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-500 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || !draftText.trim()}
              className="rounded-full bg-[#2f3e46] px-4 py-1 text-xs font-semibold text-white transition-colors hover:bg-[#1e2c33] disabled:opacity-50"
            >
              {createMutation.isPending ? 'Posting…' : 'Post'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
